import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type SaveOptions = {
  successMessage?: string;
  errorMessage?: (err: unknown) => string;
};

/**
 * Runs a save request and tells the person how it went, in a toast.
 * `save` resolves to true on success so the caller can close the editor;
 * on failure `error` holds the same message for EditPanel to show inline.
 */
export function useSaveWithToast() {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (
    request: () => Promise<unknown>,
    options: SaveOptions = {}
  ): Promise<boolean> => {
    setSaving(true);
    setError(null);
    try {
      await request();
      toast.success(options.successMessage ?? t("settings_saved"));
      return true;
    } catch (err) {
      const message = options.errorMessage
        ? options.errorMessage(err)
        : t("settings_save_failed");
      setError(message);
      toast.error(message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  return { save, saving, error };
}
